if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-predictive-search-results]');
      if (!this.input || !this.results) return;

      this.abortController = null;
      this.debounceTimer = null;
      this.cache = {};
      this.selectedIndex = -1;

      this._onInput = this._onInput.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onFocus = this._onFocus.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);

      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('keydown', this._onKeydown);
      this.input.addEventListener('focus', this._onFocus);
      document.addEventListener('click', this._onDocumentClick);
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener('input', this._onInput);
        this.input.removeEventListener('keydown', this._onKeydown);
        this.input.removeEventListener('focus', this._onFocus);
      }
      document.removeEventListener('click', this._onDocumentClick);
      clearTimeout(this.debounceTimer);
      if (this.abortController) this.abortController.abort();
    }

    _onDocumentClick(e) {
      if (!this.contains(e.target)) this._close();
    }

    _debounce(fn, delay) {
      clearTimeout(this.debounceTimer);
      var self = this;
      this.debounceTimer = setTimeout(function() { fn.call(self); }, delay);
    }

    _onInput() {
      var query = this.input.value.trim();
      if (query.length < 2) {
        this._close();
        return;
      }
      var self = this;
      this._debounce(function() { self._search(query); }, 300);
    }

    _onFocus() {
      var query = this.input.value.trim();
      if (query.length >= 2 && this.results.innerHTML.trim()) {
        this._open();
      }
    }

    _search(query) {
      if (this.cache[query]) {
        this.results.innerHTML = this.cache[query];
        this._open();
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this._showLoading();
      this._open();

      var self = this;
      var baseUrl = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      var url = baseUrl + '?q=' + encodeURIComponent(query) + '&section_id=predictive-search&resources[type]=product,collection,article,page,query&resources[limit]=4';

      fetch(url, { signal: this.abortController.signal })
        .then(function(response) {
          if (!response.ok) return;
          return response.text();
        })
        .then(function(text) {
          if (!text) return;
          var parser = new DOMParser();
          var doc = parser.parseFromString(text, 'text/html');
          var resultsEl = doc.querySelector('#predictive-search-results');

          if (resultsEl) {
            self.cache[query] = resultsEl.outerHTML;
            self.results.innerHTML = resultsEl.outerHTML;
            self.selectedIndex = -1;
          }
        })
        .catch(function(e) {
          if (e.name !== 'AbortError') console.error('Predictive search error:', e);
        });
    }

    _showLoading() {
      this.results.innerHTML = '<div class="flex items-center justify-center py-8"><div class="w-4 h-4 border border-gray-300 border-t-transparent rounded-full animate-spin"></div></div>';
    }

    _open() {
      this.results.classList.remove('opacity-0', 'pointer-events-none');
      this.results.classList.add('opacity-100', 'pointer-events-auto');
    }

    _close() {
      this.results.classList.add('opacity-0', 'pointer-events-none');
      this.results.classList.remove('opacity-100', 'pointer-events-auto');
      this.selectedIndex = -1;
    }

    _onKeydown(e) {
      var options = this.results.querySelectorAll('[role="option"]');
      if (!options.length) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.selectedIndex = Math.min(this.selectedIndex + 1, options.length - 1);
          this._updateSelection(options);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          this._updateSelection(options);
          break;
        case 'Enter':
          if (this.selectedIndex >= 0 && options[this.selectedIndex]) {
            e.preventDefault();
            var link = options[this.selectedIndex].querySelector('a');
            if (link) link.click();
          }
          break;
        case 'Escape':
          this._close();
          this.input.blur();
          break;
      }
    }

    _updateSelection(options) {
      options.forEach(function(opt, i) {
        var link = opt.querySelector('a');
        if (i === this.selectedIndex) {
          opt.setAttribute('aria-selected', 'true');
          if (link) link.classList.add('bg-gray-50');
        } else {
          opt.setAttribute('aria-selected', 'false');
          if (link) link.classList.remove('bg-gray-50');
        }
      }.bind(this));
    }
  });
}
